import { type FormEvent, useState } from 'react';
import { usePageData } from '@/contexts/pageContext';
import { applyMask } from '@/utils/applyMask';
import { removeNonDigits } from '@/utils/removeNonDigits';
import { useAddGiftCard } from './useAddGiftCard';
import { useLoadGiftCards } from './useLoadGiftCards';
import { useCheckout } from '@/contexts/checkoutContext';

const regex = /(\d{1,4})(\d{0,4})(\d{0,4})(\d{0,4})/;
const mask = '$1 $2 $3 $4';

const getMaskedCardNumber = (value: string) =>
  applyMask({ mask, regex, value });

export const useGiftCards = () => {
  const { helpText } = usePageData();
  const { checkoutData } = useCheckout();

  const storedGiftCards = checkoutData.amounts?.giftCard;
  const hasStoredCards = (storedGiftCards?.length ?? 0) > 0;

  const appliedGiftCards =
    storedGiftCards
      ?.filter(card => card.isApplied)
      .map(card => card.cardNumber) ?? [];
  const hasAppliedCards = (appliedGiftCards?.length ?? 0) > 0;

  useLoadGiftCards(hasStoredCards);

  const [cardNumber, setCardNumber] = useState('');
  const [pin, setPin] = useState('');

  const handleCardNumberChange = (value: string) => {
    const cleanedValue = removeNonDigits(value);
    const maskedValue = applyMask({ mask, regex, value: cleanedValue });

    setCardNumber(maskedValue);
  };

  const clearForm = () => {
    setCardNumber('');
    setPin('');
  };

  const { addGiftCard, isPending } = useAddGiftCard(clearForm);

  const handleAddGiftCard = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!isPending) {
      addGiftCard({ giftCardNumber: removeNonDigits(cardNumber), pin });
    }
  };

  return {
    appliedGiftCards,
    cardNumber,
    getMaskedCardNumber,
    handleAddGiftCard,
    handleCardNumberChange,
    hasAppliedCards,
    hasStoredCards,
    helpText,
    isPending,
    pin,
    setPin,
    storedGiftCards,
  };
};
